/**
 * La tarea, para leerla.
 *
 * Hasta el 2026-08-14 no existia una vista de lectura: la unica superficie de una tarea era
 * `TaskDialog`, un formulario de edicion de 448 px (`max-w-md`) con el cuerpo en 12 px y la
 * descripcion en un textarea de 60 px de alto — unas cuatro lineas—. Ahi es donde una persona
 * leia el analisis completo que escribio un agente.
 *
 * Y la entrega del agente (`output_text`) se renderizaba en **un solo lugar**: la tarjeta del
 * kanban, a 9 px, con `max-h-16`, recortada a 300 caracteres, en una columna de 200 px. Ademas
 * estaba condicionada a `assignee.kind === 'agent'`, asi que si la tarea se reasignaba a una
 * persona la entrega **desaparecia de la pantalla**.
 *
 * Aca la lectura manda: ancho comodo, tipografia normal, la descripcion y la entrega completas
 * y siempre. El dialogo queda solo para editar.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ticketsAPI, capsuleConfigAPI, formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Loader2, AlertTriangle, Clock, Bot, User } from 'lucide-react';
import { toast } from 'sonner';
import { avisarDependenciasPendientes } from '@/lib/dependenciasPendientes';
import { etiquetaDeEstadoDeTarea, TASK_TYPE_LABELS, VERIFICATION_STATUS_LABELS } from '@/components/tickets/constants';
import Markdown from '@/components/Markdown';
import AprobarHerramienta from '@/components/AprobarHerramienta';
import Autorizacion from '@/components/Autorizacion';
import { unaLineaDeBloqueo } from '@/lib/dependenciasPendientes';
import DeclararAvance from '@/components/tickets/DeclararAvance';

/**
 * El texto de una tarea. Lo escribe un agente y **es markdown**: titulos, listas y tablas.
 * Se mostraba como texto plano con los saltos de linea respetados, o sea que una tabla se veia
 * como una fila de pipes y un titulo como un renglon con almohadillas.
 */
function Prosa({ children }) {
  return <Markdown>{children}</Markdown>;
}

function Dato({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="text-sm mt-0.5">{children ?? <span className="text-zinc-300">—</span>}</p>
    </div>
  );
}

export default function TaskPage() {
  const { ticketId, taskId } = useParams();
  const [searchParams] = useSearchParams();
  const capsuleId = searchParams.get('capsule') || '';
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [horas, setHoras] = useState('');
  // La nota es obligatoria. Si fuera opcional se saltearia siempre —lo fue dos años, y por eso
  // todas las imputaciones de la base dicen `activity: null`— y `consumed_hours` volveria a ser
  // un numero que crece y despues nadie puede discutir.
  const [nota, setNota] = useState('');
  const [imputaciones, setImputaciones] = useState([]);
  // La entrega escrita de una persona. El backend se la habilita a quien tiene la tarea desde
  // siempre; lo que faltaba era el campo.
  const [entrega, setEntrega] = useState('');
  const [editandoEntrega, setEditandoEntrega] = useState(false);
  const { user } = useAuth();
  const tenantId = user?.tenant_id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await ticketsAPI.getTask(tenantId, capsuleId, ticketId, taskId);
      setTask(t.data);
      const tk = await ticketsAPI.get(tenantId, capsuleId, ticketId).catch(() => null);
      setTicket(tk?.data || null);
      const cfg = await capsuleConfigAPI.get(tenantId, capsuleId).catch(() => null);
      setTaskStatuses(cfg?.data?.task_statuses || []);
      const te = await ticketsAPI
        .listTimeEntries(tenantId, capsuleId, ticketId, { task_id: taskId })
        .catch(() => null);
      setImputaciones(te?.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId, capsuleId, ticketId, taskId]);

  useEffect(() => { if (tenantId) load(); }, [tenantId, load]);

  const cambiarEstado = async (nuevo) => {
    setBusy(true);
    try {
      const res = await ticketsAPI.patchTask(tenantId, capsuleId, ticketId, taskId, { status: nuevo });
      // Mismo motivo que en Mi trabajo: lo que esta mal se dice antes de felicitar.
      if (!avisarDependenciasPendientes(res)) toast.success('Estado actualizado');
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  /**
   * Declarar el avance desde la ficha. **No mueve el estado**, por la misma razon que la entrega:
   * «esto es lo que hice» y «esto esta terminado» son dos decisiones distintas, y juntarlas
   * obligaria a cerrar una tarea para poder contar un avance parcial.
   */
  const declararAvance = async (pct) => {
    setBusy(true);
    try {
      await ticketsAPI.patchTask(tenantId, capsuleId, ticketId, taskId, { avance_pct: pct });
      await load();
    } catch (e) { toast.error(formatApiError(e) || 'No se pudo guardar el avance'); }
    finally { setBusy(false); }
  };

  const guardarEntrega = async () => {
    const texto = entrega.trim();
    if (!texto) return;
    setBusy(true);
    try {
      // Solo `output_text`: escribir la entrega **no** mueve el estado. Son dos decisiones
      // distintas —"esto es lo que hice" y "esto esta terminado"— y juntarlas obligaria a cerrar
      // una tarea para poder contar un avance parcial.
      await ticketsAPI.patchTask(tenantId, capsuleId, ticketId, taskId, { output_text: texto });
      toast.success('Entrega guardada');
      setEditandoEntrega(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cargarHoras = async () => {
    const n = parseFloat(horas);
    if (!n || n <= 0) return;
    if (!nota.trim()) { toast.error('Falta decir en que se fueron esas horas'); return; }
    setBusy(true);
    try {
      await ticketsAPI.createTimeEntry(tenantId, capsuleId, ticketId, {
        task_id: taskId, minutes: Math.round(n * 60), activity: nota.trim(),
      });
      toast.success('Horas cargadas');
      setHoras(''); setNota('');
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  }
  if (!task) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <p className="text-sm text-zinc-500">No se encontro la tarea.</p>
        <Link to="/my-work" className="text-brand-primary text-sm hover:underline">Volver a Mi trabajo</Link>
      </div>
    );
  }

  const esAgente = task.assignee?.kind === 'agent';
  const asignado = task.assignee?.name || task.assignee?.id;
  // **Quien escribe la entrega es quien hace el trabajo.** No se adivina el permiso del backend
  // con una lista de roles: alcanza con ser el asignado, que es exactamente lo que
  // `CAMPOS_DE_MI_TAREA` habilita. Si alguien mas puede, el backend lo acepta igual y esta pantalla
  // no es la unica puerta; lo que no puede pasar es ofrecerlo a quien va a recibir un 403.
  const correo = (user?.email || '').trim().toLowerCase();
  const puedoEntregar = !esAgente && !!correo && (
    (task.assignee?.id || '').trim().toLowerCase() === correo
  );

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5" data-testid="task-page">
      <button
        type="button"
        // **A la ficha del caso, no al tablero.** Llevaba a `tab: 'board'`, que muestra las
        // tarjetas de las tareas — o sea mas de lo mismo que esta pantalla ya dice. El analisis,
        // la descripcion y el workspace con los artefactos viven en `detail`, y esa era la
        // informacion que la gente no encontraba: el 2026-09-30 alguien con todos los permisos
        // llego dos veces hasta aca y nunca vio el analisis del caso.
        onClick={() => navigate(`/tickets?${new URLSearchParams({ ticket: ticketId, capsule: capsuleId, tab: 'detail' })}`)}
        className="text-xs text-zinc-500 hover:text-brand-primary flex items-center gap-1"
      >
        <ArrowLeft className="w-3 h-3" /> {ticketId} · {ticket?.title || 'el caso'}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-[#09090B] tracking-tight break-words">
            {task.title}
          </h1>
          <p className="text-xs text-zinc-500 mt-1 flex flex-wrap items-center gap-2">
            {esAgente ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
            <span>{asignado || 'Sin asignar'}</span>
            {/* El label de la fase vive en `project.phases` del caso. Mostrar la `key` es el mismo
                defecto que el resto: un id donde iba un nombre. */}
            {task.phase && (
              <span>· fase {(ticket?.phases || []).find((f) => f.key === task.phase)?.label || task.phase}</span>
            )}
            <span>· {TASK_TYPE_LABELS[task.task_type] || task.task_type}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Que la tarea no se pudo avisar se escribia en Mongo y no lo miraba nadie: la
              persona simplemente no se enteraba de que tenia trabajo. */}
          {task.notify_error && (
            <Badge variant="destructive" className="text-[10px]" title={task.notify_error}>
              <AlertTriangle className="w-3 h-3 mr-1" /> No se pudo avisar
            </Badge>
          )}
          {/* Una autorizacion sin resolver **no se cierra moviendo el desplegable**: el backend
              responde 409, y ofrecer lo que el backend rechaza no enseña la regla, enseña que la
              aplicacion falla. Se dice por donde sí, que es el panel de arriba. */}
          <select
            className="border border-brand-border rounded-sm h-8 px-2 text-xs bg-white disabled:opacity-60"
            value={task.status || ''}
            disabled={busy || (task.task_type === 'autorizacion' && !task.autorizacion?.resultado)}
            title={task.task_type === 'autorizacion' && !task.autorizacion?.resultado
              ? 'Esta tarea se cierra autorizando o rechazando, arriba.'
              : undefined}
            onChange={(e) => cambiarEstado(e.target.value)}
            data-testid="task-page-status"
          >
            {(taskStatuses || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      </div>

      {/* **Lo que la traba va arriba, antes de la descripcion.** Esta pantalla es donde una
          persona decide si puede arrancar, y era la que menos sabia: el bloqueo por dependencia
          solo lo cargaba el kanban del caso, asi que quien hace el trabajo era el unico que no veia
          que estaba esperando a otro. No bloquea el desplegable —el solape deliberado existe y el
          plan tiene que poder representarlo— pero ya no es invisible. */}
      {(task.bloqueada_por || []).length > 0 && (
        <Card className="rounded-sm border-zinc-300 bg-zinc-50" data-testid="task-page-bloqueada">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-zinc-700 mb-1">Esta esperando a otra tarea</p>
            <ul className="text-xs text-zinc-600 space-y-0.5">
              {task.bloqueada_por.map((b) => (
                <li key={b.link_id || b.id}>· {unaLineaDeBloqueo(b)}</li>
              ))}
            </ul>
            <p className="text-[11px] text-zinc-500 mt-2">
              Se puede avanzar igual —a veces hay que solaparse— pero conviene saberlo antes.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Una autorizacion pendiente va arriba de todo: es lo unico que hay que hacer con esta
          tarea, y dejarla debajo de la descripcion la convierte en un detalle que se pasa. */}
      {task.task_type === 'aprobacion' && (
        <AprobarHerramienta
          tarea={task} tenantId={tenantId} capsuleId={capsuleId} ticketId={ticketId}
          onCambio={load}
        />
      )}
      {task.task_type === 'autorizacion' && (
        <Autorizacion tarea={task} tenantId={tenantId} onCambio={load} />
      )}

      <Card className="rounded-sm border-brand-border">
        <CardContent className="p-5 space-y-5">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Descripcion</p>
            {task.description
              ? <Prosa>{task.description}</Prosa>
              : <p className="text-sm text-zinc-400">Sin descripcion.</p>}
          </div>

          {/* Completa y **siempre**, no solo cuando la tarea sigue asignada a un agente. */}
          {task.output_text && !editandoEntrega && (
            <div className="border-t border-zinc-100 pt-4" data-testid="task-page-entrega">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {esAgente ? 'Entrega del agente' : 'La entrega'}
                </p>
                {puedoEntregar && (
                  <Button size="sm" variant="ghost" className="h-6 text-[11px]"
                    onClick={() => { setEntrega(task.output_text || ''); setEditandoEntrega(true); }}
                    data-testid="editar-entrega">
                    Editar
                  </Button>
                )}
              </div>
              <Prosa>{task.output_text}</Prosa>
            </div>
          )}

          {/* **El permiso estaba concedido y la superficie no existia.** `CAMPOS_DE_MI_TAREA` le
              habilita `output_text` a quien tiene la tarea —"su entrega: lo que hace con su
              trabajo"— y no habia un solo campo en todo el frontend para escribirlo: se podia leer
              lo que entrego un agente, y una persona no podia entregar nada por escrito. Es el
              mismo patron que `exige_resolucion` y los pesos de las plantillas: la pieza esta, el
              enganche no. */}
          {puedoEntregar && (!task.output_text || editandoEntrega) && (
            <div className="border-t border-zinc-100 pt-4 space-y-2" data-testid="escribir-entrega">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Tu entrega</p>
              <Textarea
                value={entrega}
                onChange={(e) => setEntrega(e.target.value)}
                placeholder="Que hiciste, que encontraste, que queda. Lo lee quien sigue, y queda en el caso."
                className="rounded-sm text-sm"
                rows={5}
                data-testid="entrega-texto"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" className="h-8 text-xs rounded-sm"
                  disabled={busy || !entrega.trim()} onClick={guardarEntrega}
                  data-testid="guardar-entrega">
                  Guardar la entrega
                </Button>
                {editandoEntrega && (
                  <Button size="sm" variant="ghost" className="h-8 text-xs"
                    onClick={() => { setEditandoEntrega(false); setEntrega(''); }}>
                    Cancelar
                  </Button>
                )}
                <span className="text-[11px] text-zinc-500">
                  Se guarda en la tarea; no cambia su estado.
                </span>
              </div>
            </div>
          )}

          {/* **Lo hecho y lo gastado, juntos.** El avance no se muestra solo a proposito: un
              porcentaje a mano se infla, y lo que vale es la distancia con el consumo. */}
          <div className="border-t border-zinc-100 pt-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Cuanto esta hecho</p>
            <DeclararAvance task={task} disabled={busy} onDeclarar={declararAvance} />
          </div>

          <div className="border-t border-zinc-100 pt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Dato label="Estimado">{task.estimate_hours ? `${task.estimate_hours} h` : null}</Dato>
            <Dato label="Cargadas">{task.consumed_hours ? `${task.consumed_hours} h` : null}</Dato>
            <Dato label="Inicio">{task.planned_start}</Dato>
            <Dato label="Fin planificado">{task.planned_end}</Dato>
          </div>

          {/* **El rastro de las re-estimaciones, donde se trabaja.** Es lo que convierte el desvio
              en informacion en vez de en un reproche: una tarea mal estimada deja de ser la culpa
              de alguien y pasa a ser un dato de como estima el area. */}
          {(task.reestimaciones || []).length > 0 && (
            <div className="border-t border-zinc-100 pt-4" data-testid="reestimaciones">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                Se volvio a estimar
              </p>
              <ul className="space-y-1 text-xs text-zinc-600">
                {task.reestimaciones.map((r, i) => (
                  <li key={i}>
                    <span className="font-mono text-[11px]">{r.de} h → {r.a} h</span>
                    {' · '}{r.motivo}
                    <span className="text-zinc-400"> · {r.por}{r.at ? ` · ${String(r.at).slice(0, 10)}` : ''}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {task.verification?.required && (
            <div className="border-t border-zinc-100 pt-4">
              <Dato label="Verificacion">
                <Badge variant="outline" className="text-[10px]">
                  {VERIFICATION_STATUS_LABELS[task.verification.status] || task.verification.status || 'Pendiente'}
                </Badge>
              </Dato>
            </div>
          )}

          <div className="border-t border-zinc-100 pt-4 flex items-end gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Cargar horas</p>
              <Input
                type="number" min="0" step="0.25" value={horas}
                onChange={(e) => setHoras(e.target.value)}
                className="h-8 text-xs rounded-sm w-24 mt-0.5" placeholder="h"
                data-testid="task-page-horas"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">En que se fueron</p>
              <Input
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && horas && nota.trim()) cargarHoras(); }}
                className="h-8 text-xs rounded-sm mt-0.5"
                placeholder="Reproduje el error con el comprobante de Cordoba"
                maxLength={500}
                data-testid="task-page-nota"
              />
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm shrink-0"
              disabled={busy || !horas || !nota.trim()} onClick={cargarHoras}>
              <Clock className="w-3 h-3 mr-1" /> Cargar
            </Button>
          </div>

          {/* Las horas cargadas, con lo que se hizo en cada una. Antes esta pantalla mostraba
              solo el total: cuatro horas contra una tarea, sin decir en que se fueron, no se
              pueden discutir ni aprender. */}
          {imputaciones.length > 0 && (
            <div className="border-t border-zinc-100 pt-3 space-y-1" data-testid="task-page-imputaciones">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">
                Imputaciones ({imputaciones.length})
              </p>
              <div className="max-h-40 overflow-y-auto divide-y divide-zinc-50">
                {imputaciones.map((e) => (
                  <div key={e.entry_id} className="py-1.5 flex items-baseline gap-2 text-xs">
                    <span className="font-mono text-[10px] text-zinc-400 shrink-0">{e.date}</span>
                    <span className="font-mono text-[11px] shrink-0 w-12 text-right">
                      {(e.minutes / 60).toFixed(2)}h
                    </span>
                    <span className={`flex-1 min-w-0 truncate ${e.activity ? '' : 'text-zinc-400 italic'}`}
                      title={e.activity || 'Se cargo antes de que la nota fuera obligatoria'}>
                      {e.activity || 'sin nota'}
                    </span>
                    <span className="text-[10px] text-zinc-400 shrink-0">{e.actor_name || ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-zinc-400">
        Estado actual: {etiquetaDeEstadoDeTarea(taskStatuses, task.status)}
        {task.created_at ? ` · creada ${String(task.created_at).slice(0, 16).replace('T', ' ')}` : ''}
      </p>
    </div>
  );
}
